import { AccountBird } from "./AccountBird";
import { useState } from "react";
import type { AccountScope } from "@proof/accounts";
import { Button, Control } from "./components";
import { AccountEmail } from "./AccountEmail";
import { ConfirmAccountPassword, type AccountWork } from "./AccountSecurity";
import { accountRequest, type AdministrationView } from "./account-api";
const key = (scope: AccountScope) =>
  scope.kind === "global" ? "global" : `${scope.kind}:${scope.id}`;
export function AccountAdministration({
  data,
  run,
  refresh,
}: {
  data: AdministrationView;
  run: AccountWork;
  refresh: () => Promise<void>;
}) {
  const [tab, setTab] = useState(
      new URLSearchParams(location.search).get("tab") || "people",
    ),
    [scope, setScope] = useState(key(data.scopes[0])),
    [inviteName, setInviteName] = useState(""),
    [inviteEmail, setInviteEmail] = useState(""),
    [inviteRole, setInviteRole] = useState("administrator"),
    [inviteDelivery, setInviteDelivery] = useState<"link" | "email">("link"),
    [setupLink, setSetupLink] = useState<{
      url: string;
      email: string;
      expiresAt: number;
      id: string;
    }>(),
    [siteName, setSiteName] = useState(""),
    [siteEdit, setSiteEdit] = useState<string>(),
    [companyName, setCompanyName] = useState(""),
    [companyEdit, setCompanyEdit] = useState<string>(),
    [companySite, setCompanySite] = useState(data.sites[0]?.id || ""),
    [grantUser, setGrantUser] = useState(
      data.users.find((user) => !user.root)?.id || "",
    ),
    [grantRole, setGrantRole] = useState("editor"),
    [policyRequired, setPolicyRequired] = useState(
      data.policies[key(data.scopes[0])]?.required || false,
    ),
    [emailMethod, setEmailMethod] = useState(
      data.policies[key(data.scopes[0])]?.methods.includes("email") ?? true,
    ),
    [totpMethod, setTotpMethod] = useState(
      data.policies[key(data.scopes[0])]?.methods.includes("totp") ?? true,
    ),
    [settings, setSettings] = useState({
      name: data.settings?.name || "",
      locale: data.settings?.locale || "en-US",
      timezone: data.settings?.timezone || "America/Chicago",
      sessionHours: data.settings?.sessionHours || 8,
    }),
    [clientId, setClientId] = useState(data.settings?.google?.clientId || ""),
    [clientSecret, setClientSecret] = useState(""),
    [domain, setDomain] = useState(data.settings?.google?.domain || "");
  const chosen =
      data.scopes.find((value) => key(value) === scope) || data.scopes[0],
    label = (scope: AccountScope) =>
      scope.kind === "global"
        ? "Application"
        : scope.kind === "site"
          ? `Site: ${data.sites.find((site) => site.id === scope.id)?.name || scope.id}`
          : `Company: ${data.companies.find((company) => company.id === scope.id)?.name || scope.id}`;
  const scopePicker = (
    <Control label="Administration scope">
      <select
        value={scope}
        onChange={(event) => {
          setSetupLink(undefined);
          setScope(event.target.value);
          const policy = data.policies[event.target.value];
          setPolicyRequired(policy?.required || false);
          setEmailMethod(policy ? policy.methods.includes("email") : true);
          setTotpMethod(policy ? policy.methods.includes("totp") : true);
        }}
      >
        {data.scopes.map((scope) => (
          <option key={key(scope)} value={key(scope)}>
            {label(scope)}
          </option>
        ))}
      </select>
    </Control>
  );
  const invoke = (path: string, body: unknown, message: string) =>
    run(async () => {
      await accountRequest(path, body);
      await refresh();
    }, message);
  const canSite = (id: string) =>
    data.scopes.some(
      (scope) =>
        scope.kind === "global" || (scope.kind === "site" && scope.id === id),
    );
  return (
    <main className="account-content">
      <h1>Administration</h1>
      <p>
        Manage application defaults, sites, companies and their access. Site
        managers can manage every company in their site.
      </p>
      <ConfirmAccountPassword run={run} />
      <nav aria-label="Administration sections" className="account-tabs">
        {[
          ["people", "People & invitations"],
          ["workspaces", "Sites & companies"],
          ["email", "Email providers"],
          ["bird", "Bird integrations"],
          ["security", "Two-factor policies"],
          ...(data.settings
            ? [
                ["defaults", "Global defaults"],
                ["requests", "Access requests"],
                ["google", "Google sign-in"],
              ]
            : []),
          ["audit", "Activity"],
        ].map(([id, name]) => (
          <Button
            key={id}
            kind={tab === id ? "primary" : "secondary"}
            onClick={() => setTab(id)}
          >
            {name}
          </Button>
        ))}
      </nav>
      {tab === "bird" && (
        <>
          {scopePicker}
          <AccountBird
            key={scope}
            data={data}
            scope={chosen}
            run={run}
            refresh={refresh}
          />
        </>
      )}
      {tab === "requests" && data.settings && (
        <section>
          <h2>Access requests</h2>
          <p>
            Review requests from the public website. Prepare an invitation to
            choose the workspace and role before issuing access.
          </p>
          {!data.accessRequests.length && <p>No access requests yet.</p>}
          {data.accessRequests.map((item) => (
            <article className="account-card" key={item.id}>
              <h3>
                {item.name} · {item.company}
              </h3>
              <p>
                {item.email} · {new Date(item.createdAt).toLocaleString()} ·{" "}
                {item.status}
              </p>
              <p style={{ whiteSpace: "pre-wrap" }}>{item.message}</p>
              {item.status === "pending" && (
                <>
                  <Button
                    onClick={() => {
                      setInviteName(item.name);
                      setInviteEmail(item.email);
                      setScope(
                        key(
                          data.scopes.find(
                            (value) => value.kind === "company",
                          ) || chosen,
                        ),
                      );
                      setSetupLink(undefined);
                      setTab("people");
                    }}
                  >
                    Prepare invitation
                  </Button>
                  <Button
                    onClick={() =>
                      void invoke(
                        "/access-requests/close",
                        { id: item.id },
                        "Access request closed.",
                      )
                    }
                  >
                    Close request
                  </Button>
                </>
              )}
            </article>
          ))}
        </section>
      )}
      {tab === "email" && (
        <AccountEmail administration={data} run={run} refresh={refresh} />
      )}
      {tab === "people" && (
        <>
          <h2>Invite a user</h2>
          <p>
            Generate a one-use setup link to send yourself, without configuring
            email. New users must set their password before they can sign in;
            existing users confirm their current password. Links expire after 72
            hours.
          </p>
          {scopePicker}
          <form
            className="account-form"
            onSubmit={(event) => {
              event.preventDefault();
              setSetupLink(undefined);
              void run(
                async () => {
                  const issued = await accountRequest<{
                    id: string;
                    url?: string;
                    expiresAt: number;
                  }>("/invitations", {
                    name: inviteName,
                    email: inviteEmail,
                    scope: chosen,
                    role: inviteRole,
                    root: chosen.kind === "global",
                    delivery: inviteDelivery,
                  });
                  if (issued.url)
                    setSetupLink({
                      ...issued,
                      url: issued.url,
                      email: inviteEmail,
                    });
                  setInviteName("");
                  setInviteEmail("");
                  await refresh();
                },
                inviteDelivery === "link"
                  ? "Setup link created. Copy it and send it privately to the user."
                  : "Invitation sent by email.",
              );
            }}
          >
            <div className="control-grid">
              <Control label="Invitee name">
                <input
                  required
                  value={inviteName}
                  onChange={(event) => setInviteName(event.target.value)}
                />
              </Control>
              <Control label="Invitee email">
                <input
                  required
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                />
              </Control>
              <Control label="Invited role">
                <select
                  disabled={chosen.kind === "global"}
                  value={inviteRole}
                  onChange={(event) => setInviteRole(event.target.value)}
                >
                  <option value="administrator">
                    {chosen.kind === "global"
                      ? "Global administrator"
                      : chosen.kind === "site"
                        ? "Site manager"
                        : "Company manager"}
                  </option>
                  <option value="editor">Editor</option>
                  <option value="tester">Proof user</option>
                  <option value="assetManager">Asset manager</option>
                </select>
              </Control>
              <Control label="Invitation delivery">
                <select
                  value={inviteDelivery}
                  onChange={(event) =>
                    setInviteDelivery(event.target.value as "link" | "email")
                  }
                >
                  <option value="link">
                    Copy setup link (no email required)
                  </option>
                  <option value="email">Send by email</option>
                </select>
              </Control>
            </div>
            <Button kind="primary" type="submit">
              {inviteDelivery === "link"
                ? "Create setup link"
                : "Send invitation"}
            </Button>
          </form>
          {setupLink && (
            <section aria-label="New user setup link" className="account-form">
              <p>
                Send this link privately to {setupLink.email}. It expires{" "}
                {new Date(setupLink.expiresAt).toLocaleString()} and works once.
                The link is shown only here; if you lose it, generate a
                replacement below.
              </p>
              <Control label="One-use setup link">
                <input
                  readOnly
                  value={setupLink.url}
                  autoComplete="off"
                  spellCheck={false}
                  onFocus={(event) => event.target.select()}
                />
              </Control>
              <Button
                onClick={() =>
                  void run(async () => {
                    await navigator.clipboard.writeText(setupLink.url);
                  }, "Setup link copied.")
                }
              >
                Copy setup link
              </Button>
              <Button onClick={() => setSetupLink(undefined)}>
                Dismiss setup link
              </Button>
            </section>
          )}
          <h2>Users</h2>
          <div className="account-table">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Access</th>
                  <th>Security</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.name}</strong>
                      <small>{user.email}</small>
                    </td>
                    <td>
                      {user.root
                        ? "Global administrator"
                        : data.grants
                            .filter(
                              (grant) =>
                                grant.subjectId === user.id && grant.active,
                            )
                            .map((grant) => (
                              <div key={grant.id}>
                                {label(
                                  grant.scope === "site"
                                    ? { kind: "site", id: grant.siteId }
                                    : { kind: "company", id: grant.companyId },
                                )}{" "}
                                · {grant.roleIds.join(", ")}
                                <Button
                                  onClick={() =>
                                    void invoke(
                                      "/grants",
                                      {
                                        userId: user.id,
                                        scope:
                                          grant.scope === "site"
                                            ? { kind: "site", id: grant.siteId }
                                            : {
                                                kind: "company",
                                                id: grant.companyId,
                                              },
                                        role: grant.roleIds.includes(
                                          "tenant.administrator",
                                        )
                                          ? "administrator"
                                          : grant.roleIds.includes(
                                                "tenant.editor",
                                              )
                                            ? "editor"
                                            : grant.roleIds.includes(
                                                  "tenant.tester",
                                                )
                                              ? "tester"
                                              : "assetManager",
                                        active: false,
                                      },
                                      "Access revoked.",
                                    )
                                  }
                                >
                                  Revoke this access
                                </Button>
                              </div>
                            ))}
                    </td>
                    <td>
                      {user.totpEnabled ? "Authenticator · " : ""}
                      {user.emailMfa ? "Email codes · " : ""}
                      {user.googleLinked ? "Google connected" : "Password"}
                    </td>
                    <td>
                      {user.active ? "Active" : "Suspended"}
                      {data.settings && (
                        <Button
                          onClick={() =>
                            void invoke(
                              "/users/status",
                              { userId: user.id, active: !user.active },
                              "Account status updated.",
                            )
                          }
                        >
                          {user.active ? "Suspend account" : "Activate account"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {chosen.kind !== "global" && (
            <form
              className="account-form"
              onSubmit={(event) => {
                event.preventDefault();
                void invoke(
                  "/grants",
                  {
                    userId: grantUser,
                    scope: chosen,
                    role: grantRole,
                    active: true,
                  },
                  "Access assigned.",
                );
              }}
            >
              <h3>Assign access to an existing user</h3>
              <Control label="Existing user">
                <select
                  required
                  value={grantUser}
                  onChange={(event) => setGrantUser(event.target.value)}
                >
                  <option value="">Choose a user</option>
                  {data.users
                    .filter((user) => !user.root)
                    .map((user) => (
                      <option value={user.id} key={user.id}>
                        {user.name} · {user.email}
                      </option>
                    ))}
                </select>
              </Control>
              <Control label="Assigned role">
                <select
                  value={grantRole}
                  onChange={(event) => setGrantRole(event.target.value)}
                >
                  <option value="administrator">
                    {chosen.kind === "site"
                      ? "Site manager"
                      : "Company manager"}
                  </option>
                  <option value="editor">Editor</option>
                  <option value="tester">Proof user</option>
                  <option value="assetManager">Asset manager</option>
                </select>
              </Control>
              <Button type="submit">Assign scoped access</Button>
            </form>
          )}
          <h2>Invitations</h2>
          {data.invites.length === 0 ? (
            <p>No invitations yet.</p>
          ) : (
            data.invites.map((invite) => (
              <div className="account-session" key={invite.id}>
                <span>
                  {invite.email} · {label(invite.scope)} ·{" "}
                  {invite.usedAt
                    ? "Accepted"
                    : invite.revokedAt
                      ? "Revoked"
                      : invite.expiresAt < Date.now()
                        ? "Expired"
                        : invite.delivery === "pending"
                          ? "Awaiting acceptance"
                          : invite.delivery}
                </span>
                {!invite.usedAt && !invite.revokedAt && (
                  <>
                    <Button
                      onClick={() => {
                        setSetupLink(undefined);
                        void run(async () => {
                          const issued = await accountRequest<{
                            id: string;
                            url: string;
                            expiresAt: number;
                          }>("/invitations", {
                            name: invite.name,
                            email: invite.email,
                            scope: invite.scope,
                            root: invite.root,
                            role: invite.role,
                            delivery: "link",
                          });
                          setSetupLink({ ...issued, email: invite.email });
                          await refresh();
                        }, "Replacement setup link created. The previous link no longer works.");
                      }}
                    >
                      Generate new setup link
                    </Button>
                    <Button
                      onClick={() => {
                        setSetupLink(undefined);
                        void invoke(
                          "/invitations/revoke",
                          { id: invite.id },
                          "Invitation revoked.",
                        );
                      }}
                    >
                      Revoke invitation
                    </Button>
                  </>
                )}
              </div>
            ))
          )}
        </>
      )}
      {tab === "workspaces" && (
        <>
          <h2>Sites</h2>
          {data.sites.map((site) => (
            <div className="account-session" key={site.id}>
              <span>
                {site.name} · {site.active ? "Active" : "Inactive"}
              </span>
              {canSite(site.id) && (
                <Button
                  onClick={() => {
                    setSiteName(site.name);
                    setSiteEdit(site.id);
                  }}
                >
                  Edit site
                </Button>
              )}
            </div>
          ))}
          {(data.settings || siteEdit) && (
            <form
              className="account-form"
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  await accountRequest("/sites", {
                    ...(siteEdit ? { id: siteEdit } : {}),
                    name: siteName,
                    active: true,
                  });
                  setSiteName("");
                  setSiteEdit(undefined);
                  await refresh();
                }, "Site saved.");
              }}
            >
              <Control label="Site name">
                <input
                  required
                  value={siteName}
                  onChange={(event) => setSiteName(event.target.value)}
                />
              </Control>
              <Button type="submit">
                {siteEdit ? "Save site" : "Create site"}
              </Button>
            </form>
          )}
          <h2>Companies</h2>
          {data.companies.map((company) => (
            <div className="account-session" key={company.id}>
              <span>
                {company.name} ·{" "}
                {data.sites.find((site) => site.id === company.siteId)?.name} ·{" "}
                {company.active ? "Active" : "Inactive"}
              </span>
              <Button
                onClick={() => {
                  setCompanyEdit(company.id);
                  setCompanyName(company.name);
                  setCompanySite(company.siteId);
                }}
              >
                Edit company
              </Button>
            </div>
          ))}
          <form
            className="account-form"
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                await accountRequest("/companies", {
                  ...(companyEdit ? { id: companyEdit } : {}),
                  siteId: companySite,
                  name: companyName,
                  active: true,
                });
                setCompanyName("");
                setCompanyEdit(undefined);
                await refresh();
              }, "Company saved.");
            }}
          >
            <div className="control-grid">
              <Control label="Company site">
                <select
                  value={companySite}
                  disabled={!!companyEdit}
                  onChange={(event) => setCompanySite(event.target.value)}
                >
                  {data.sites
                    .filter((site) => canSite(site.id) || companyEdit)
                    .map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                </select>
              </Control>
              <Control label="Company name">
                <input
                  required
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                />
              </Control>
            </div>
            <Button type="submit">
              {companyEdit ? "Save company" : "Create company"}
            </Button>
          </form>
        </>
      )}
      {tab === "security" && (
        <>
          <h2>Two-factor policies</h2>
          <p>
            Requirements apply from application to site to company. Child
            settings can require verification or narrow the available methods;
            they cannot weaken a parent requirement.
          </p>
          {scopePicker}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void invoke(
                "/security/policy",
                {
                  scope: chosen,
                  policy: {
                    required: policyRequired,
                    methods: [
                      ...(emailMethod ? ["email"] : []),
                      ...(totpMethod ? ["totp"] : []),
                    ],
                  },
                },
                "Two-factor policy saved.",
              );
            }}
          >
            <label className="account-check">
              <input
                type="checkbox"
                checked={policyRequired}
                onChange={(event) => setPolicyRequired(event.target.checked)}
              />{" "}
              Require two-factor authentication
            </label>
            <label className="account-check">
              <input
                type="checkbox"
                checked={emailMethod}
                onChange={(event) => setEmailMethod(event.target.checked)}
              />{" "}
              Allow email codes
            </label>
            <label className="account-check">
              <input
                type="checkbox"
                checked={totpMethod}
                onChange={(event) => setTotpMethod(event.target.checked)}
              />{" "}
              Allow authenticator apps
            </label>
            <Button kind="primary" type="submit">
              Save two-factor policy
            </Button>
          </form>
          <h3>Saved policies</h3>
          {Object.entries(data.policies).map(([scope, policy]) => (
            <p key={scope}>
              {scope === "global"
                ? "Application"
                : label(
                    data.scopes.find((value) => key(value) === scope) || {
                      kind: "global",
                    },
                  )}
              : {policy.required ? "Required" : "Optional"} ·{" "}
              {policy.methods
                .map((method) =>
                  method === "totp" ? "Authenticator app" : "Email",
                )
                .join(" / ")}
            </p>
          ))}
        </>
      )}
      {tab === "defaults" && data.settings && (
        <>
          <h2>Global defaults</h2>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void invoke("/settings", settings, "Global defaults saved.");
            }}
          >
            <div className="control-grid">
              <Control label="Application name">
                <input
                  required
                  value={settings.name}
                  onChange={(event) =>
                    setSettings({ ...settings, name: event.target.value })
                  }
                />
              </Control>
              <Control label="Default locale">
                <input
                  required
                  value={settings.locale}
                  onChange={(event) =>
                    setSettings({ ...settings, locale: event.target.value })
                  }
                />
              </Control>
              <Control label="Default timezone">
                <input
                  required
                  value={settings.timezone}
                  onChange={(event) =>
                    setSettings({ ...settings, timezone: event.target.value })
                  }
                />
              </Control>
              <Control label="Session lifetime (hours)">
                <input
                  type="number"
                  min={1}
                  max={24}
                  required
                  value={settings.sessionHours}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      sessionHours: Number(event.target.value),
                    })
                  }
                />
              </Control>
            </div>
            <Button type="submit" kind="primary">
              Save global defaults
            </Button>
          </form>
          <p>
            Company document defaults, shared forms, profiles, validation
            presets and output settings remain available in Configuration after
            opening that company.
          </p>
        </>
      )}
      {tab === "google" && data.settings && (
        <>
          <h2>Google sign-in</h2>
          <p>
            Configure a Web application OAuth client. Users connect their Google
            identity under Account & security before using Google to sign in.
          </p>
          <p>
            <a
              href={data.links.googleCredentials}
              target="_blank"
              rel="noreferrer"
            >
              Create Google OAuth credentials
            </a>{" "}
            · Redirect URI:{" "}
            <code>{data.publicOrigin}/api/accounts/google/callback</code>
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                await accountRequest("/google/configuration", {
                  clientId,
                  ...(clientSecret ? { clientSecret } : {}),
                  ...(domain ? { domain } : {}),
                });
                setClientSecret("");
                await refresh();
              }, "Google sign-in configured.");
            }}
          >
            <Control label="Google sign-in client ID">
              <input
                required
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
              />
            </Control>
            <Control label="Google sign-in client secret">
              <input
                type="password"
                autoComplete="new-password"
                value={clientSecret}
                placeholder={
                  data.settings.google
                    ? "Stored secret retained when blank"
                    : ""
                }
                onChange={(event) => setClientSecret(event.target.value)}
              />
            </Control>
            <Control label="Restrict Google Workspace domain (optional)">
              <input
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
              />
            </Control>
            <Button kind="primary" type="submit">
              Save Google sign-in
            </Button>
          </form>
        </>
      )}
      {tab === "audit" && (
        <>
          <h2>Recent account activity</h2>
          <div className="account-table">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Scope</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {[...data.audit].reverse().map((event) => (
                  <tr key={event.id}>
                    <td>{new Date(event.at).toLocaleString()}</td>
                    <td>{event.action.replaceAll("_", " ")}</td>
                    <td>{event.scope ? label(event.scope) : "Application"}</td>
                    <td>{event.target || event.actor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}
